import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { VStack, Input, useToast } from "@chakra-ui/react";
import { User as FormEntity, UserApi as FormEntityApi } from "../../../apis";
import { AppFormControl, AppLoader } from "../../../components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import { APPC } from "../../../config";

export const UserForm: FC<{
    formContainerState: FormContainerStateReturnType;
}> = ({ formContainerState }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<FormEntity>(new FormEntity());
    const isEditMode: boolean = !!formContainerState.entityId;

    // form
    const {
        handleSubmit,
        register,
        formState: { errors },
        reset,
    } = useForm<FormEntity>({
        resolver: yupResolver(schema(t, isEditMode)),
        mode: "onSubmit",
    });

    const onSubmitHandler = async (formData: FormEntity) => {
        setLoading(true);
        FormEntityApi.createOrUpdate<FormEntity, FormEntity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!formContainerState.entityId) {
            return;
        }
        setLoading(true);
        FormEntityApi.getItem<FormEntity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    setData(response);
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    return (
        <>
            {loading ? (
                <AppLoader />
            ) : (
                <AppFormContainer
                    title={t("padm.UserForm:text.pageTitle")}
                    state={formContainerState}
                    type={APPC.FORM_CONTAINER}
                >
                    <form
                        id="app-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit(onSubmitHandler)();
                        }}
                    >
                        <VStack gap={5} alignItems={"stretch"}>
                            <AppFormControl
                                label={t("ent.User:firstName.label")}
                                isInvalid={!!errors?.firstName}
                                message={errors?.firstName?.message}
                            >
                                <Input
                                    type="text"
                                    placeholder={t(
                                        "ent.User:firstName.placeholder"
                                    )}
                                    {...register("firstName")}
                                    defaultValue={data.firstName}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.User:lastName.label")}
                                isInvalid={!!errors?.lastName}
                                message={errors?.lastName?.message}
                            >
                                <Input
                                    type="text"
                                    placeholder={t(
                                        "ent.User:lastName.placeholder"
                                    )}
                                    {...register("lastName")}
                                    defaultValue={data.lastName}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.User:email.label")}
                                isInvalid={!!errors?.email}
                                message={errors?.email?.message}
                            >
                                <Input
                                    type="email"
                                    placeholder={t(
                                        "ent.User:email.placeholder"
                                    )}
                                    {...register("email")}
                                    defaultValue={data.email}
                                    disabled={isEditMode}
                                />
                            </AppFormControl>
                            {!isEditMode && (
                                <>
                                    <AppFormControl
                                        label={t(
                                            "ent.User:plainPassword.label"
                                        )}
                                        isInvalid={!!errors?.plainPassword}
                                        message={errors?.plainPassword?.message}
                                    >
                                        <Input
                                            type="password"
                                            placeholder={t(
                                                "ent.User:plainPassword.placeholder"
                                            )}
                                            {...register("plainPassword")}
                                        />
                                    </AppFormControl>
                                    <AppFormControl
                                        label={t(
                                            "ent.User:plainPasswordConfirm.label"
                                        )}
                                        isInvalid={
                                            !!errors?.plainPasswordConfirm
                                        }
                                        message={
                                            errors?.plainPasswordConfirm
                                                ?.message
                                        }
                                    >
                                        <Input
                                            type="password"
                                            placeholder={t(
                                                "ent.User:plainPasswordConfirm.placeholder"
                                            )}
                                            {...register(
                                                "plainPasswordConfirm"
                                            )}
                                        />
                                    </AppFormControl>
                                </>
                            )}
                        </VStack>
                    </form>
                </AppFormContainer>
            )}
        </>
    );
};
